import { KitButton } from './button';
import { useId, useRef, type ReactNode } from 'react';
import type { FormController, FormSnapshot } from '../tier0/form-controller.mjs';
import { useSortableGesture } from './sortable-gesture';

export interface SortableItem {
  id: string;
  label: string;
}
export function SortableList<T extends SortableItem>({
  items,
  controller,
  newOperationId,
  renderItem,
  renderActions,
  canMove,
  copy,
  onError,
}: {
  items: T[];
  controller: FormController;
  newOperationId(): string;
  renderItem(item: T): ReactNode;
  renderActions?(item: T, move: (beforeId: string | null) => void): ReactNode;
  canMove?(item: T): boolean;
  copy: {
    move(item: T): string;
    position(item: T, index: number, count: number): string;
    status(snapshot: FormSnapshot): string | null;
  };
  onError(error: unknown): void;
}) {
  const prefix = useId();
  const list = useRef<HTMLOListElement>(null);
  const touch = useRef<{ x: number; y: number } | null>(null);
  const byId = new Map(items.map((item) => [item.id, item]));
  const drag = useSortableGesture(
    items.map((item) => item.id),
    controller,
    newOperationId,
    (id, position, count) => copy.position(byId.get(id)!, position, count),
  );
  const order = drag.gesture?.order ?? items.map((item) => item.id);
  const drop = () => {
    const item = drag.gesture && byId.get(drag.gesture.id);
    if (!item || (canMove && !canMove(item))) {
      drag.cancel();
      return;
    }
    void drag.drop().catch(onError);
  };
  const point = (x: number, y: number) => {
    const element = list.current?.ownerDocument
      .elementFromPoint(x, y)
      ?.closest<HTMLElement>('.pc-sort-item');
    if (element && list.current?.contains(element)) drag.preview(element.dataset.itemId!);
  };
  const status = copy.status(drag.snapshot);
  return (
    <>
      <ol className="pc-sort-list" ref={list}>
        {order.map((id) => {
          const item = byId.get(id)!;
          const allowed = canMove?.(item) ?? true;
          return (
            <li
              key={id}
              id={`${prefix}-${id}`}
              className="pc-sort-item"
              data-item-id={id}
              data-state={
                drag.gesture?.id === id
                  ? 'dragging'
                  : drag.gesture && order.indexOf(id) === order.indexOf(drag.gesture.id) + 1
                    ? 'drop-before'
                    : undefined
              }
              onDragOver={(event) => {
                if (drag.gesture) {
                  event.preventDefault();
                  drag.preview(id);
                }
              }}
              onDrop={(event) => {
                event.preventDefault();
                drop();
              }}
            >
              <KitButton
                variant="quiet"
                type="button"
                className="pc-drag-handle"
                aria-label={copy.move(item)}
                disabled={drag.locked || !allowed}
                draggable={!drag.locked && allowed}
                onDragStart={() => {
                  if (allowed) drag.begin(id);
                }}
                onDragEnd={drag.cancel}
                onPointerDown={(event) => {
                  if (allowed && event.pointerType !== 'mouse') {
                    touch.current = { x: event.clientX, y: event.clientY };
                    event.currentTarget.setPointerCapture(event.pointerId);
                    drag.longPress(id);
                  }
                }}
                onPointerMove={(event) => {
                  if (drag.gesture?.id === id && event.pointerType !== 'mouse') {
                    event.preventDefault();
                    point(event.clientX, event.clientY);
                  } else if (
                    touch.current &&
                    Math.hypot(event.clientX - touch.current.x, event.clientY - touch.current.y) >=
                      4
                  )
                    drag.clearTimer();
                }}
                onPointerUp={() => {
                  drag.clearTimer();
                  if (drag.gesture?.id === id) drop();
                }}
                onPointerCancel={drag.cancel}
                onKeyDown={(event) => {
                  if (![' ', 'ArrowUp', 'ArrowDown', 'Escape'].includes(event.key)) return;
                  if (!allowed || drag.locked) return;
                  event.preventDefault();
                  if (event.key === 'Escape') drag.cancel();
                  else if (event.key === ' ') {
                    if (drag.gesture?.id === id) drop();
                    else drag.begin(id);
                  } else drag.step(id, event.key === 'ArrowUp' ? -1 : 1);
                }}
              />
              {renderItem(item)}
              {allowed &&
                renderActions?.(item, (beforeId) => {
                  if (!drag.locked) void drag.commitIntent(id, beforeId).catch(onError);
                })}
            </li>
          );
        })}
      </ol>
      <div role="status" aria-live="polite">
        {status ?? drag.message}
      </div>
    </>
  );
}
